import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useAppDispatch } from '@/shared/hooks/store';
import {
    CustomersController,
    ICustomersLoginResponse,
    ICustomersVerificationCodeRequest,
    ICustomersVerificationNewPassRequest,
    ICustomerVerificationCodeResponse
} from '@/shared/endpoints/customers';
import { toast } from 'react-toastify';
import { setToken } from '@/store/slices/auth';

// Components
import ForgotPassword from './forgot-password';
import ConfirmPassword from './confirm-password';
import ResetSuccess from './reset-success';
import VerifyCodeV2 from '@/modules/auth/components/register-form-v2/verify-code';
import { ICustomerVerifyCodeResponse } from '@/shared/endpoints/customers';
import { unfulfilledGenerateVerificationCodeAlertHandler } from '@/shared/helpers/generateCode';
import { IErrorResponse } from '@/shared/types/interfaces';

interface Props {
    initialEmail: string;
}

const ResetPassword = ({ initialEmail }: Props) => {
    const dispatch = useAppDispatch();
    const [activeStep, setActiveStep] = useState(1);
    const [userData, setUserData] = useState<ICustomersLoginResponse>({} as ICustomersLoginResponse);
    const [code, setCode] = useState('');
    const [currentSource, setCurrentSource] = useState<ICustomerVerificationCodeResponse>();
    const [verificationPayload, setVerificationPayload] = useState<ICustomersVerificationCodeRequest>({
        email: ''
    });

    const sendVerificationCode = useCallback(
        async (step: number, email: string, variant: 'email' | 'phone'): Promise<void> => {

            setVerificationPayload(prev => {
                prev.email = email;

                if (variant === 'email') {
                    prev.type = 'EMAIL';
                } else if (variant === 'phone') {
                    delete prev.type;
                }

                return prev;
            });

            await dispatch(CustomersController.generateVerificationCode(verificationPayload)).then(({ meta, payload }) => {
                if (meta.requestStatus === 'fulfilled') {
                    setActiveStep(step);
                    if (payload && 'source' in payload) {
                        setCurrentSource(payload);
                        toast.success(`Verification code sent to ${payload.source}`);
                    }
                } else {
                    if (variant === 'phone') {
                        sendVerificationCode(2, email, 'email');
                    }
                    else {
                        unfulfilledGenerateVerificationCodeAlertHandler(payload as IErrorResponse)
                    }
                }
            });
        },
        [dispatch, verificationPayload]
    );

    const handleVerifyCode = useCallback(
        (step: number, res: {
            code: string;
            payload: ICustomerVerifyCodeResponse;
        }) => {
            const { code, payload } = res;
            setCode(code);
            setActiveStep(step);
            setUserData(payload);
        },
        []
    );

    const loginToAccount = useCallback(() => {
        dispatch(setToken(userData));
    }, [userData, dispatch]);

    const handleConfirmPassword = useCallback(
        async (step: number, data: { pass: string }) => {
            const payload: ICustomersVerificationNewPassRequest = {
                code,
                email: verificationPayload.email,
                pass: data.pass
            };

            await dispatch(CustomersController.confirmNewPassword(payload)).then(({ meta }) => {
                if (meta.requestStatus === 'fulfilled') setActiveStep(step);
            });
        },
        [code, dispatch, verificationPayload.email]
    );

    // step two here needs to be replace with the verify code component
    const Steps = useMemo(
        (): { [key: string]: ReactNode } => ({
            1: <ForgotPassword initialEmail={initialEmail} handleChangeStep={(email: string) => sendVerificationCode(2, email, 'phone')} />,
            2: (
                <VerifyCodeV2
                    initialSourceForDelivery={currentSource?.source || ''}
                    email={verificationPayload.email}
                    onGetCode={payload => handleVerifyCode(3, payload)}
                    initialSignUpOtpTarget={currentSource?.channel === 'EMAIL' ? 'email' : 'phone'}
                    onlyCode
                    design="small"
                    troubleWithModal={false}
                    customHeader="Verify account to reset your password"
                />
            ),
            3: <ConfirmPassword handleChangeStep={(step: number, data: { pass: string }) => handleConfirmPassword(step, data)} />,
            4: <ResetSuccess loginToAccount={loginToAccount} />
        }),
        [
            verificationPayload.email,
            sendVerificationCode,
            handleConfirmPassword,
            handleVerifyCode,
            loginToAccount,
            initialEmail,
            currentSource
        ]
    );

    return <>{Steps[activeStep]}</>;
};

export default ResetPassword;
